import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Lock,
  CreditCard,
  Send,
  Wallet,
  ChevronRight,
  Home,
  Zap,
  Server,
  ShoppingCart,
  Receipt,
  Coins
} from 'lucide-react';

interface NavItem {
  id: string;
  title: string;
  href: string;
  icon?: React.ComponentType<any>;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  {
    id: 'overview',
    title: 'Overview',
    href: '/',
    icon: Home,
  },
  {
    id: 'getting-started',
    title: 'Getting Started',
    href: '/getting-started',
    icon: Zap,
  },
  {
    id: 'authentication',
    title: 'Authentication',
    href: '/authentication',
    icon: Lock,
  },
  {
    id: 'collections',
    title: 'Collections',
    href: '/collections',
    icon: CreditCard,
    children: [
      {
        id: 'reserved-accounts-individual',
        title: 'Reserved Accounts - Individual',
        href: '/collections/reserved-individual',
      },
      {
        id: 'reserved-accounts-corporate',
        title: 'Reserved Accounts - Corporate',
        href: '/collections/reserved-corporate',
      },
      {
        id: 'temporary-accounts',
        title: 'Temporary Accounts',
        href: '/collections/temporary-accounts',
      },
      {
        id: 'simulate-transfer',
        title: 'Simulate Transfer',
        href: '/collections/simulate-transfer',
      },
      {
        id: 'reserve-account-transaction-status',
        title: 'Reserve Account - TSQ',
        href: '/collections/reserve-account-transaction-status',
      },
      {
        id: 'static-transaction-status',
        title: 'Static Virtual Account - TSQ',
        href: '/collections/static-transaction-status',
      },
      {
        id: 'dynamic-transaction-status',
        title: 'Dynamic Virtual Account - TSQ',
        href: '/collections/dynamic-transaction-status',
      },
      {
        id: 'third-party-virtual-account',
        title: 'Third-Party Virtual Account',
        href: '/collections/third-party-virtual-account',
      },
    ],
  },
  // {
  //   id: 'server-to-server',
  //   title: 'Server to Server',
  //   href: '/server-to-server',
  //   icon: Server,
  //   children: [
  //     {
  //       id: 'environments',
  //       title: 'Environments',
  //       href: '/server-to-server/environments',
  //     },
  //     {
  //       id: 'charge-card-3ds',
  //       title: 'Charge a Card (3DS)',
  //       href: '/server-to-server/charge-card-3ds',
  //     },
  //     {
  //       id: 'charge-card-2ds',
  //       title: 'Charge a Card (2DS)',
  //       href: '/server-to-server/charge-card-2ds',
  //     },
  //     {
  //       id: 'charge-card-otp',
  //       title: 'Charge a Card (OTP Auth)',
  //       href: '/server-to-server/charge-card-otp',
  //     },
  //     {
  //       id: 'authorize-card-transaction',
  //       title: 'Authorize a Card Transaction',
  //       href: '/server-to-server/authorize-card-transaction',
  //     },
  //     {
  //       id: 'verify-transaction-status',
  //       title: 'Verify Transaction Status',
  //       href: '/server-to-server/verify-transaction-status',
  //     },
  //     {
  //       id: 'refund',
  //       title: 'Refund',
  //       href: '/server-to-server/refund',
  //     },
  //     {
  //       id: 'status-code',
  //       title: 'Status Code',
  //       href: '/server-to-server/status-code',
  //     },
  //     {
  //       id: 'transaction-history',
  //       title: 'Transaction History',
  //       href: '/server-to-server/transaction-history',
  //     },
  //     {
  //       id: 'webhook',
  //       title: 'Webhook',
  //       href: '/server-to-server/webhook',
  //     },
  //   ],
  // },
  {
    id: 'gateway-checkout',
    title: 'Gateway Checkout',
    href: '/gateway-checkout',
    icon: ShoppingCart,
  },
  {
    id: 'payouts',
    title: 'Payouts',
    href: '/payouts',
    icon: Send,
    children: [
      {
        id: 'banks',
        title: 'Bank List',
        href: '/payouts/banks',
      },
      {
        id: 'name-enquiry',
        title: 'Name Enquiry',
        href: '/payouts/name-enquiry',
      },
      {
        id: 'transfers',
        title: 'Initiate Transfer',
        href: '/payouts/transfers',
      },
      {
        id: 'transfer-status',
        title: 'Transfer Status',
        href: '/payouts/transfer-status',
      },
    ],
  },
  {
    id: 'account-management',
    title: 'Account Management',
    href: '/account',
    icon: Wallet,
    children: [
      {
        id: 'reserve-account-details',
        title: 'Reserve Account Details',
        href: '/account/balance',
      },
      {
        id: 'single-account',
        title: 'Account Balance',
        href: '/account/single-balance',
      },
    ],
  },
  {
    id: 'bill-payment',
    title: 'Bill Payment',
    href: '/bill-payment',
    icon: Receipt,
    children: [
      {
        id: 'get-bill-payment-categories',
        title: 'Product Categories',
        href: '/bill-payment/categories',
      },
      {
        id: 'get-service-providers',
        title: 'Service Providers',
        href: '/bill-payment/services',
      },
      {
        id: 'get-all-products',
        title: 'All Products',
        href: '/bill-payment/products-all',
      },
      {
        id: 'get-products-paged',
        title: 'Products (Paged)',
        href: '/bill-payment/products',
      },
      {
        id: 'get-product-by-id',
        title: 'Product By Id',
        href: '/bill-payment/product',
      },
      {
        id: 'get-customer-information',
        title: 'Customer Information',
        href: '/bill-payment/customer-information',
      },
      {
        id: 'initiate-bill-payment',
        title: 'Initiate Payment',
        href: '/bill-payment/initiate',
      },
      {
        id: 'authorize-bill-payment',
        title: 'Authorize Payment',
        href: '/bill-payment/authorize',
      },
    ],
  },
  {
    id: 'customer-crypto',
    title: 'Customer (Crypto)',
    href: '/customer-crypto',
    icon: Coins,
    children: [
      {
        id: 'register-customer',
        title: 'Register Customer',
        href: '/customer-crypto/register',
      },
      {
        id: 'verify-email',
        title: 'Verify Email',
        href: '/customer-crypto/verify-email',
      },
      {
        id: 'resend-verification',
        title: 'Resend Verification',
        href: '/customer-crypto/resend-verification',
      },
      {
        id: 'add-identification',
        title: 'Add Identification (KYC)',
        href: '/customer-crypto/identification',
      },
      {
        id: 'selfie-compare',
        title: 'Selfie Compare',
        href: '/customer-crypto/selfie-compare',
      },
      {
        id: 'get-quote',
        title: 'Get Quote',
        href: '/customer-crypto/quote',
      },
      {
        id: 'accept-quote',
        title: 'Accept Quote',
        href: '/customer-crypto/accept-quote',
      },
      {
        id: 'reject-quote',
        title: 'Reject Quote',
        href: '/customer-crypto/reject-quote',
      },
      {
        id: 'check-payment-status',
        title: 'Payment Status',
        href: '/customer-crypto/payment-status',
      },
    ],
  },
];

export const Sidebar: React.FC = () => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = React.useState<string[]>(['collections']);

  const toggleExpanded = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId) ? [] : [itemId]
    );
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const handleSubItemClick = (item: NavItem, _event: React.MouseEvent) => {
    // If it's a sub-item (has parent), scroll to the corresponding section
    if (item.href.includes('/')) {
      const pathParts = item.href.split('/');
      const sidebarPath = pathParts[pathParts.length - 1]; // Get the last part of the URL
      const parentPath = pathParts[pathParts.length - 2]; // Get the parent section
      
      // Map sidebar paths to actual endpoint IDs with context
      const getEndpointId = (parent: string, path: string): string => {
        const mappings: Record<string, Record<string, string>> = {
          // Payouts mappings
          'payouts': {
            'banks': 'get-banks',
            'name-enquiry': 'name-enquiry',
            'transfers': 'initiate-transfer',
            'transfer-status': 'get-transfer-status',
          },
          // Collections mappings
          'collections': {
            'reserved-individual': 'create-reserved-individual',
            'reserved-corporate': 'create-reserved-corporate',
            'reserve-account-transaction-status': 'reserve-account-transaction-status',
            'temporary-accounts': 'create-temporary-static',
            'static-transaction-status': 'static-transaction-status',
            'dynamic-transaction-status': 'dynamic-transaction-status',
            'simulate-transfer': 'fund-transfer-single-credit',
            'third-party-virtual-account': 'create-third-party-virtual-account',
          },
          // Server to Server mappings
          'server-to-server': {
            'environments': 'environments',
            'webhook': 'webhook',
            'charge-card-3ds': 'charge-card-3ds',
            'charge-card-2ds': 'charge-card-2ds',
            'charge-card-otp': 'charge-card-otp',
            'authorize-card-transaction': 'authorize-card-transaction',
            'verify-transaction-status': 'verify-transaction-status',
            'refund': 'refund',
            'status-code': 'status-code',
            'transaction-history': 'transaction-history',
          },
          // Gateway Checkout mappings
          'gateway-checkout': {
            'webhook': 'gateway-webhook',
          },
          // Account Management mappings
          'account': {
            'balance': 'reserve-account-details',
            'single-balance': 'single-account'
          },
          // Bill Payment mappings
          'bill-payment': {
            'categories': 'get-bill-payment-categories',
            'services': 'get-service-providers',
            'products-all': 'get-all-products',
            'products': 'get-products-paged',
            'product': 'get-product-by-id',
            'customer-information': 'get-customer-information',
            'initiate': 'initiate-bill-payment',
            'authorize': 'authorize-bill-payment',
          },
          // Customer Crypto mappings
          'customer-crypto': {
            'register': 'register-customer',
            'verify-email': 'verify-email',
            'resend-verification': 'resend-verification',
            'identification': 'add-identification',
            'selfie-compare': 'selfie-compare',
            'quote': 'get-quote',
            'accept-quote': 'accept-quote',
            'reject-quote': 'reject-quote',
            'payment-status': 'check-payment-status',
          }
        };
        
        return mappings[parent]?.[path] || path;
      };
      
      const endpointId = getEndpointId(parentPath, sidebarPath);
      
      // Small delay to allow navigation to complete
      setTimeout(() => {
        const element = document.getElementById(endpointId);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  };

  const renderNavItem = (item: NavItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const active = isActive(item.href);

    return (
      <div key={item.id} className="relative">
        <div className="flex items-center">
          <Link
            to={item.href}
            onClick={(e) => {
              if (level > 0) {
                handleSubItemClick(item, e);
              } else if (hasChildren) {
                toggleExpanded(item.id);
              }
            }}
            className={`flex-1 flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
              active
                ? level > 0
                  ? 'text-lime'
                  : 'bg-lime text-white shadow-lg'
                : 'text-sidebar-text hover:bg-white/10 hover:text-white'
            } ${level > 0 ? 'ml-6' : ''}`}
          >
            {item.icon && level === 0 && (
              <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
            )}
            {level > 0 && (
              <div className={`mr-3 h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                active ? 'bg-lime' : 'bg-white/30'
              }`} />
            )}
            <span className="truncate">{item.title}</span>
          </Link>
          {hasChildren && (
            <button
              onClick={() => toggleExpanded(item.id)}
              className={`p-1.5 mr-2 rounded-md transition-all duration-200 ${
                isExpanded ? 'transform rotate-90' : ''
              } ${
                active ? 'text-white' : 'text-sidebar-text hover:text-white hover:bg-white/10'
              }`}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="mt-1 space-y-1">
            {item.children!.map(child => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <nav className="p-6 space-y-6">
      <div className="mb-8">
        <h2 className="px-2 text-xs font-semibold text-white/50 uppercase tracking-wider">
          API Documentation
        </h2>
      </div>
      
      <div className="space-y-2">
        {navItems.map(item => renderNavItem(item))}
      </div>

      {/* <div className="mt-12 px-2">
        <div className="border-t border-white/10 pt-8">
          <h3 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-4">
            Resources
          </h3>
          <div className="space-y-1">
            <a
              href="https://aku.africa/support"
              className="flex items-center px-3 py-2 text-sm text-sidebar-text hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Book className="h-4 w-4 mr-3" />
              Support
            </a>
            <a
              href="https://aku.africa/changelog"
              className="flex items-center px-3 py-2 text-sm text-sidebar-text hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
              target="_blank"
              rel="noopener noreferrer"
            >
              <CreditCard className="h-4 w-4 mr-3" />
              Changelog
            </a>
          </div>
        </div>
      </div> */}
    </nav>
  );
};